import { isEmpty } from 'lodash'

import {
  DueListFilterPopover,
  DueListTableFilterData,
  DuelistFilterDataBy,
  DuelistFilterProjection,
  DuelistFilterTimeframeCycles,
  DuelistFilterTimeframeDays,
  DuelistFilterTimeframeHours,
  DuelistFilterTimeframeLandings,
  DuelistGeneralFilterData,
  DuelistTrackedByFilterData,
  SelectedInProgress,
  UnknownBooleanObject,
} from 'src/types'
import { isNotEmpty } from 'src/utils/helpers'

import { RootState } from '../reducers'
import {
  initialProjectionFilterData,
  initialFilterData,
  setDuelistFiltersData,
  setDuelistColumnsData,
  setShowChildItems as setShowChildItemsReducer,
  dueListItemSelected,
  dueListItemUnselected,
  unselectAllMaintenanceItems as unselectAllDueListItems,
  initialGeneralFilterData,
} from '../slices/dueListTableSlice'

import { useDispatch } from './useDispatch'
import { useSelector } from './useSelector'
import {
  setMaintenanceItemColumnsData,
  setMaintenanceItemFiltersData,
  setShowChildMaintenanceItems,
  unselectAllMaintenanceItems,
  maintenanceItemSelected,
  maintenanceItemUnselected,
} from 'src/slices/maintenanceItemTableSlice'
import { MaintenanceItem } from 'types/graphql'
import {
  removeAircraft,
  addAircraft,
  resetAircrafts,
} from 'src/slices/persistentSession'

export type TableVariant = 'duelist' | 'maintenanceItems'

type TimeframeFilters = {
  timeFrameAll: boolean
  timeframeDays: DuelistFilterTimeframeDays
  timeframeHours: DuelistFilterTimeframeHours
  timeframeCycles: DuelistFilterTimeframeCycles
  timeframeLandings: DuelistFilterTimeframeLandings
}

interface DueListTableFiltersHook {
  duelistFiltersData: DueListTableFilterData
  setDuelistFilters: (data: DueListTableFilterData) => void
  setFuzzySearchTerms: (searchTerms: string) => void
  setPowerSearchTerms: (searchTerms: any) => void
  setDueStatus: (key: string, value: boolean) => void
  setInterval: (key: string, value: boolean) => void
  setTimeframeAll: (timeframe: boolean) => void
  setTimeframeDays: (timeframe: DuelistFilterTimeframeDays) => void
  setTimeframeHours: (timeframe: DuelistFilterTimeframeHours) => void
  setTimeframeCycles: (timeframe: DuelistFilterTimeframeCycles) => void
  setTimeframeLandings: (timeframe: DuelistFilterTimeframeLandings) => void
  setAircraftFilter: (key: string, value: boolean) => void
  setProjectionFilters: (value: DuelistFilterProjection) => void
  setDefaultDynamicFilters: (aircraftFilters: UnknownBooleanObject) => void
  setAssignedMechanicFilter: (key: string, value: boolean) => void
  setFilterDataBy: (value: DuelistFilterDataBy[]) => void
  setDueListFilterPopoverFilters: (value: DueListFilterPopover) => void
  areGeneralFiltersApplied: () => boolean
  removeGeneralFilters: () => void
  areTrackedByFiltersApplied: () => boolean
  removeTrackedByFilters: () => void
  isAtaCodeFilterApplied: () => boolean
  removeAtaCodeFilter: () => void
  unselectAll: () => void
  setTableRefreshKey: () => void
  resetAircraftFilter: () => void
  areProjectionsApplied: () => boolean
  removeProjectionFilters: () => void
  resetFilters: () => void
  getSearchConfig: () => { value: any; variables: any }
  //local
  setEnabledColumns: (columns: string[]) => void
  enabledColumns: string[]
  setShowChildItems: (showChildItems: boolean) => void
  showChildItems: boolean
  aircraftFilter: DueListTableFilterData['aircrafts']

  // Selection
  selectMaintenanceItem: (item: MaintenanceItem | MaintenanceItem[]) => void
  deselectMaintenanceItem: (item: MaintenanceItem | MaintenanceItem[]) => void
  selectedItemIds: string[]
  selectedInProgressItems: SelectedInProgress

  clearProjectionsAndFilters: () => void
}

const useMaintenanceItemTable = (
  storeType: TableVariant
): DueListTableFiltersHook => {
  const dispatch = useDispatch()
  const maintenanceItemTableState = useSelector((state: RootState) =>
    storeType === 'maintenanceItems'
      ? state.maintenanceItemTable
      : state.dueListTable
  )
  const filterData = maintenanceItemTableState.filters

  const setEnabledColumns = (columns: string[]): void => {
    if (storeType === 'maintenanceItems') {
      dispatch(setMaintenanceItemColumnsData(columns))
    } else {
      dispatch(setDuelistColumnsData(columns))
    }
  }

  const setShowChildItems = (showChildItems: boolean): void => {
    if (storeType === 'maintenanceItems') {
      dispatch(setShowChildMaintenanceItems(showChildItems))
    } else {
      dispatch(setShowChildItemsReducer(showChildItems))
    }
  }

  const setDuelistFilters = (data: DueListTableFilterData): void => {
    if (storeType === 'maintenanceItems') {
      dispatch(setMaintenanceItemFiltersData(data))
    } else {
      dispatch(setDuelistFiltersData(data))
    }
  }

  const unselectAll = () => {
    if (storeType === 'maintenanceItems') {
      dispatch(unselectAllMaintenanceItems())
    } else {
      dispatch(unselectAllDueListItems())
    }
  }

  const selectMaintenanceItem = (item: MaintenanceItem) => {
    if (storeType === 'maintenanceItems') {
      dispatch(maintenanceItemSelected(item))
    } else {
      dispatch(dueListItemSelected(item))
    }
  }

  const deselectMaintenanceItem = (item: MaintenanceItem) => {
    if (storeType === 'maintenanceItems') {
      dispatch(maintenanceItemUnselected(item))
    } else {
      dispatch(dueListItemUnselected(item))
    }
  }

  const setFuzzySearchTerms = (searchTerms: string): void => {
    setDuelistFilters({
      ...filterData,
      fuzzySearchTerms: searchTerms,
      powerSearchTerms: {},
    })
  }

  const setPowerSearchTerms = (searchTerms: any): void => {
    setDuelistFilters({
      ...filterData,
      powerSearchTerms: searchTerms,
      fuzzySearchTerms: '',
    })
  }
  const setDueStatus = (key: string, value: boolean): void => {
    setDuelistFilters({
      ...filterData,
      dueStatus: {
        ...filterData.dueStatus,
        [key]: value,
      },
    })
  }

  const setInterval = (key: string, value: boolean): void => {
    setDuelistFilters({
      ...filterData,
      interval: {
        ...filterData.interval,
        [key]: value,
      },
    })
  }

  const resetTimeframes = (): TimeframeFilters => ({
    timeFrameAll: false,
    timeframeDays: {
      option: '',
      days: -1,
    },
    timeframeHours: {
      hours: -1,
      option: '',
    },
    timeframeCycles: {
      cycles: -1,
      option: '',
    },
    timeframeLandings: {
      landings: -1,
      option: '',
    },
  })

  const setDuelistFilterTimeframe = (data: Partial<TimeframeFilters>): void => {
    setDuelistFilters({
      ...filterData,
      ...resetTimeframes(),
      //resetting projections when individual timeframe is selected
      projectionFilterData: initialProjectionFilterData,
      ...data,
    })
  }

  const setTimeframeAll = (all: boolean): void => {
    setDuelistFilterTimeframe({ timeFrameAll: all })
  }

  const setTimeframeDays = (data: DuelistFilterTimeframeDays): void => {
    setDuelistFilterTimeframe({ timeframeDays: data })
  }

  const setTimeframeHours = (data: DuelistFilterTimeframeHours): void => {
    setDuelistFilterTimeframe({ timeframeHours: data })
  }

  const setTimeframeCycles = (data: DuelistFilterTimeframeCycles): void => {
    setDuelistFilterTimeframe({ timeframeCycles: data })
  }

  const setTimeframeLandings = (data: DuelistFilterTimeframeLandings): void => {
    setDuelistFilterTimeframe({ timeframeLandings: data })
  }

  const setAircraftFilter = (key: string, value: boolean): void => {
    if (value) {
      dispatch(addAircraft(key))
    } else {
      dispatch(removeAircraft(key))
    }
  }

  const resetAircraftFilter = (): void => {
    setDuelistFilters({
      ...filterData,
      aircrafts: {},
    })

    // this is to persist the selected aircrafts across pages in a unified way
    dispatch(resetAircrafts())
  }

  const setDefaultDynamicFilters = (
    aircraftFilters: UnknownBooleanObject
  ): void => {
    setDuelistFilters({
      ...filterData,
      aircrafts: aircraftFilters,
    })
  }

  const setAssignedMechanicFilter = (key: string, value: boolean): void => {
    setDuelistFilters({
      ...filterData,
      assignedMechanic: {
        ...filterData.assignedMechanic,
        [key]: value,
      },
    })
  }

  const setFilterDataBy = (value: DuelistFilterDataBy[]): void => {
    setDuelistFilters({
      ...filterData,
      filterDataBy: value,
      tableRefreshKey: filterData.tableRefreshKey + 1,
    })
  }

  const setTableRefreshKey = (): void => {
    setDuelistFilters({
      ...filterData,
      tableRefreshKey: filterData.tableRefreshKey + 1,
    })
  }

  const setProjectionFilters = (value: DuelistFilterProjection): void => {
    setDuelistFilters({
      ...filterData,
      ...resetTimeframes(),
      //resetting individual timeframe when projections are selected
      timeFrameAll: true,
      projectionFilterData: value,
    })
  }

  const setGeneralFilterData = (value: DuelistGeneralFilterData): void => {
    setDuelistFilters({
      ...filterData,
      generalFilterData: value,
    })
  }

  const setAtaCodeFilterData = (value: string[]): void => {
    setDuelistFilters({
      ...filterData,
      ataCodes: value,
    })
  }

  const setTrackedByFilterData = (value: string[]): void => {
    setDuelistFilters({
      ...filterData,
      trackedBy: value,
    })
  }

  const setDueListFilterPopoverFilters = (
    value: DueListFilterPopover
  ): void => {
    const { projections, generalFilters, ataCodes, trackedBy } = value
    setDuelistFilters({
      ...filterData,
      ...resetTimeframes(),
      //resetting individual timeframe when projections are selected
      timeFrameAll: true,
      projectionFilterData: projections,
      generalFilterData: generalFilters,
      ataCodes,
      trackedBy,
    })
  }

  const removeProjectionFilters = (): void => {
    const data = { ...initialProjectionFilterData }
    data.date = undefined
    data.projectionGrid = data.projectionGrid.map((projection) => {
      return { ...projection, days: 1 }
    })
    setProjectionFilters(data)
  }

  const areProjectionsApplied = (): boolean => {
    const { projectionFilterData } = filterData
    return (
      //ignore the undefined values in initialProjectionFilterData
      projectionFilterData.date !== undefined &&
      projectionFilterData.date !== ''
    )
  }

  const removeGeneralFilters = (): void => {
    const data = { ...initialGeneralFilterData }
    setGeneralFilterData(data)
  }

  const removeTrackedByFilters = (): void => {
    setTrackedByFilterData([])
  }

  const clearProjectionsAndFilters = (): void => {
    const initialProjections = { ...initialProjectionFilterData }
    initialProjections.date = undefined
    initialProjections.projectionGrid = initialProjections.projectionGrid.map(
      (projection) => {
        return { ...projection, days: 1 }
      }
    )

    setDuelistFilters({
      ...filterData,
      ...resetTimeframes(),
      //resetting individual timeframe when projections are selected
      timeFrameAll: true,
      projectionFilterData: initialProjections,
      generalFilterData: initialGeneralFilterData,
      ataCodes: [],
      trackedBy: [],
    })
  }

  const areGeneralFiltersApplied = (): boolean => {
    // const {
    //   generalFilterData: { statuses, cadenceType, isOptional, isPartBased },
    // } = filterData
    const { generalFilterData } = filterData
    return (
      (generalFilterData?.statuses && generalFilterData.statuses.length > 0) ||
      (generalFilterData?.cadenceType &&
        generalFilterData.cadenceType !== '') ||
      generalFilterData?.isOptional ||
      generalFilterData?.isPartBased ||
      generalFilterData?.isAdSb !== null ||
      generalFilterData?.enabledStatus !== 'ENABLED'
    )
  }

  const areTrackedByFiltersApplied = (): boolean => {
    const { trackedBy } = filterData
    return trackedBy?.length > 0
  }

  const removeAtaCodeFilter = (): void => {
    setAtaCodeFilterData([])
  }

  const isAtaCodeFilterApplied = (): boolean => {
    const { ataCodes } = filterData
    return ataCodes?.length > 0
  }

  const resetFilters = (): void => {
    setDuelistFilters(initialFilterData)
  }

  const getSearchConfig = () => {
    const { powerSearchTerms, fuzzySearchTerms } = filterData
    if (isEmpty(powerSearchTerms) && isNotEmpty(fuzzySearchTerms)) {
      return {
        value: fuzzySearchTerms,
        variables: { fuzzySearch: fuzzySearchTerms },
      }
    }
    if (isEmpty(fuzzySearchTerms) && isNotEmpty(powerSearchTerms)) {
      return {
        value: powerSearchTerms,
        variables: { powerSearch: powerSearchTerms },
      }
    }
    return { value: {}, variables: {} }
  }

  return {
    duelistFiltersData: filterData,
    setFuzzySearchTerms,
    setPowerSearchTerms,
    setDueStatus,
    setInterval,
    setTimeframeAll,
    setTimeframeCycles,
    setTimeframeDays,
    setTimeframeHours,
    setAircraftFilter,
    setAssignedMechanicFilter,
    setDefaultDynamicFilters,
    resetAircraftFilter,
    setFilterDataBy,
    setTableRefreshKey,
    setProjectionFilters,
    setDueListFilterPopoverFilters,
    areGeneralFiltersApplied,
    removeGeneralFilters,
    isAtaCodeFilterApplied,
    removeAtaCodeFilter,
    areTrackedByFiltersApplied,
    removeTrackedByFilters,
    setDuelistFilters,
    setTimeframeLandings,
    removeProjectionFilters,
    areProjectionsApplied,
    resetFilters,
    getSearchConfig,
    setEnabledColumns,
    enabledColumns: maintenanceItemTableState.columnsToShow,
    setShowChildItems,
    showChildItems: maintenanceItemTableState.showChildItems,
    aircraftFilter: filterData.aircrafts,

    // Selection
    selectMaintenanceItem,
    deselectMaintenanceItem,
    unselectAll,
    selectedItemIds: maintenanceItemTableState.selectedItems,
    selectedInProgressItems: maintenanceItemTableState.selectedInProgressItems,

    clearProjectionsAndFilters,
  }
}

export default useMaintenanceItemTable
